export interface TangerLayerSpec {
  url: string
  generation: number
  widths: number[]
  minZ: number
  maxZ: number
  bounds?: number[] | null
}

const DEFAULT_TILE_SIZE = 256

/**
 * The tile width to request, in pixels.
 *
 * `preferredWidth` selects the largest width the layer offers that does not
 * exceed it; without one, the layer's first offered width wins. A larger tile
 * covers proportionally more ground, so MapLibre requests one zoom level lower
 * and the same view costs a quarter of the requests at 512 — which matters
 * because the heatmap tileserver admits a bounded number of concurrent
 * requests and rejects the overflow with 503 during a pan.
 *
 * This is deliberately per-layer rather than global: heatmap rendering is
 * zoom-agnostic, so drawing it from a lower zoom level looks identical, while
 * a base map's cartography is authored per zoom level and would render the
 * wrong level's styling at the wrong scale.
 */
export function tileSizeOf(spec: TangerLayerSpec, preferredWidth?: number): number {
  const offered = spec.widths ?? []
  if (preferredWidth !== undefined) {
    const withinPreference = offered.filter((w) => w <= preferredWidth)
    if (withinPreference.length > 0) return Math.max(...withinPreference)
  }
  return offered[0] ?? DEFAULT_TILE_SIZE
}

/**
 * Converts a Tanger tile template into one MapLibre can substitute.
 *
 * Tanger's template is `{G}/{W}/{Z}/{Y}/{X}`: generation and tile width are
 * constants for a given layer, so they are baked in here, while MapLibre fills
 * the remaining three. Note that Tanger orders Y before X, the opposite of the
 * usual z/x/y convention — renaming in place rather than reassembling the path
 * is what keeps that ordering intact.
 */
export function toMapLibreTileUrl(spec: TangerLayerSpec, preferredWidth?: number): string {
  return spec.url
    .replace('{G}', String(spec.generation))
    .replace('{W}', String(tileSizeOf(spec, preferredWidth)))
    .replace('{Z}', '{z}')
    .replace('{Y}', '{y}')
    .replace('{X}', '{x}')
}
